import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'

import api from '~/api'
import { logEvent } from '~/utils/events'
import { datadogRum } from '@datadog/browser-rum'

/**
 * Hook to call the POST /user/authenticate/ endpoint after Auth0 login.
 *
 * This API creates the User record early in the signup flow (bootstrap step)
 * before proceeding with person/member creation. The response shape matches
 * the /user/me/ response shape for compatibility.
 *
 * The returned async function reads the current `email_verified` claim from the
 * Auth0 ID token and forwards it as the request body's `email_verified` field.
 * When the call is for a new, unverified account, the BE returns 403
 * `email_not_verified`. The hook intercepts that, navigates to
 * `/emailverification`, and resolves to `null`.
 */
export const useUserAuthenticated = () => {
  const history = useHistory()
  const { getIdTokenClaims } = useAuth0()
  return useCallback(async () => {
    const claims = await getIdTokenClaims()
    const emailVerified = Boolean(claims?.email_verified)
    try {
      const user = await api.rest.post('/user/authenticate/', {
        email_verified: emailVerified,
      })
      logEvent('USER_AUTHENTICATED_SUCCESS', {})
      datadogRum.setUser({
        id: String(user.id),
        email: user.email,
      })
      return user
    } catch (err: any) {
      if (err?.status === 403 && err?.data?.errorKey === 'email_not_verified') {
        history.push('/emailverification')
        return null
      }
      throw err
    }
  }, [history, getIdTokenClaims])
}
